import {
  SandboxClient,
  SandboxEvent,
  SandboxGood,
  SandboxPayment,
  SandboxWorker,
} from '../types';

const methods = ['CARD', 'CASH', 'COMPOSITE'] as const;

export const createPaymentHistory = (
  clients: SandboxClient[],
  goods: SandboxGood[],
  anchorMs: number
): SandboxPayment[] =>
  Array.from({ length: 24 }, (_, index) => {
    const client = clients[index % clients.length];
    const good = goods[index % goods.length];
    const sum = good.cost * ((index % 2) + 1);
    const method = methods[index % methods.length];
    return {
      __typename: 'Payment',
      id: 900 - index,
      method,
      client: {
        __typename: 'User',
        uuid: client.uuid,
        nickname: client.nickname,
        phone: client.phone,
      },
      sum,
      sums: { bonus: 0, cashback: 0, deposit: 0, promo_code: 0 },
      bonus: 0,
      created_at: new Date(
        anchorMs - (index < 3 ? index * 2 * 60000 : (index - 2) * 12 * 3600000)
      ).toISOString(),
      is_refunded: index === 19,
      not_refundable: false,
      items: [
        {
          id: (900 - index) * 10,
          amount: (index % 2) + 1,
          type: 'GOOD',
          sum,
          entity_id: good.id,
          entity: {
            __typename: 'Good',
            id: good.id,
            title: good.title,
            cost: good.cost,
            amount: good.amount,
            vat: good.vat,
            tax_system: good.tax_system,
          },
        },
      ],
      card_sum:
        method === 'CASH'
          ? 0
          : method === 'COMPOSITE'
          ? Math.round(sum / 2)
          : sum,
      cash_sum:
        method === 'CARD'
          ? 0
          : method === 'COMPOSITE'
          ? sum - Math.round(sum / 2)
          : sum,
      status: 'PAID',
      client_session: null,
    };
  });

export const createEventHistory = (
  clients: SandboxClient[],
  payments: SandboxPayment[],
  workers: SandboxWorker[],
  anchorMs: number
): SandboxEvent[] =>
  Array.from({ length: 96 }, (_, index) => {
    const client = clients[index % clients.length];
    const worker = workers[index % workers.length];
    const payment = payments[index % payments.length];
    const timestamp = new Date(anchorMs - index * 30 * 60000).toISOString();
    const common = {
      timestamp,
      description: 'Демонстрационное событие песочницы',
      additional_data: null,
      additional_seconds: null,
      client: {
        uuid: client.uuid,
        phone: client.phone,
        nickname: client.nickname,
        first_name: client.first_name,
        last_name: client.last_name,
      },
      worker,
      deposit: client.deposit,
    };
    const host = { title: `PC-${String((index % 24) + 1).padStart(2, '0')}` };
    const booking = {
      id: 500 + index,
      cost: 0,
      from: new Date(anchorMs + (index + 1) * 3600000).toISOString(),
      to: new Date(anchorMs + (index + 2) * 3600000).toISOString(),
    };
    const clientSession = {
      id: 600 + index,
      elapsed: 3600,
      expires_at: new Date(anchorMs + 3600000).toISOString(),
      finished_at: new Date(anchorMs + 3600000).toISOString(),
      time_left: 3600,
      is_per_minute: false,
      total_cost: 350,
    };

    switch (index % 12) {
      case 0:
        return {
          ...common,
          type: 'PAYMENT_CREATED',
          payment: {
            id: payment.id,
            title: payment.method,
            value: payment.sum,
          },
          payment_items: payment.items.map((item) => ({
            id: item.id,
            entity_type: item.type,
            title: item.entity?.title ?? item.type,
            amount: item.amount,
            sum: item.sum,
          })),
        };
      case 1:
        return {
          ...common,
          type: 'PAYMENT_REFUND',
          payment: { id: payment.id, title: 'Возврат', value: payment.sum },
        };
      case 2:
        return {
          ...common,
          type: 'DEPOSIT_UPDATED',
          value1: client.deposit - 200,
          value2: client.deposit,
          comment: 'Локальная корректировка',
        };
      case 3:
        return {
          ...common,
          type: 'CLIENT_SESSION_STARTED',
          host,
          client_session: clientSession,
          duration: 3600,
        };
      case 4:
        return {
          ...common,
          type: 'CLIENT_SESSION_FINISHED',
          host,
          client_session: clientSession,
        };
      case 5:
        return {
          ...common,
          type: 'CLIENT_SESSION_HOST_CHANGED',
          host,
          host2: { title: `PC-${String((index % 24) + 2).padStart(2, '0')}` },
          client_session: clientSession,
        };
      case 6:
        return {
          ...common,
          type: 'SEND_HOST_COMMAND',
          host,
          hosts: [{ id: index + 1, value: index + 1, title: host.title }],
          host_command: { title: 'SEND_WAKE_PACKET' },
        };
      case 7:
        return {
          ...common,
          type: 'BOOKING_CREATED',
          booking,
          hosts: [{ id: index + 1, value: index + 1, title: host.title }],
        };
      case 8:
        return {
          ...common,
          type: 'BOOKING_CANCELED',
          booking,
          hosts: [{ id: index + 1, value: index + 1, title: host.title }],
        };
      case 9:
        return {
          ...common,
          type: 'BOOKING_UPDATED',
          additional_data: '{}',
          booking,
          hosts: [{ id: index + 1, value: index + 1, title: host.title }],
        };
      case 10:
        return { ...common, type: 'USER_BANNED' };
      default:
        return { ...common, type: 'USER_UNBANNED' };
    }
  });
